import {
  useEffect,
  useState,
  useImperativeHandle,
  useMemo,
  RefObject,
} from "react";
import { useQueryClient, useMutation } from "react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { Tooltip } from "@components/new/Tooltip";
import { Alert, AlertType } from "@components/new/Alert";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import {
  IconButton,
  IconButtonSize,
  IconButtonType,
} from "@components/new/IconButton";
import TextInputContainer from "@containers/TextInputContainer";
import { linkSendCode } from "@api/apiClient";
import { validateAliKey, createAliSeller } from "@api/apiClient/sellers";
import { ConfirmEmail, ConfirmState } from "../ConfirmEmail";
import { FormHandle } from "./types";
import { getErrorMessage } from "@utils/errorMessage";
import InfoIcon from "@assets/icons/outline/info.svg?react";
import styles from "./AliForm.module.scss";

export interface AliFormType {
  email: string;
  apiKey: string;
  name: string;
}

enum steps {
  EMAIL = "email",
  CONFIRM = "confirm",
}

const AliForm = ({
  formRef,
  onStatusChange,
  onSuccess,
  onError,
  knownEmails,
}: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean;
    isLoading?: boolean;
    title?: string;
  }) => void;
  onSuccess: () => void;
  onError: () => void;
  knownEmails: string[];
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(steps.EMAIL);
  const [ticket, setTicket] = useState("");
  const [pin, setPin] = useState("");
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    disabled: false,
    loading: false,
  });

  const queryClient = useQueryClient();
  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid },
  } = useForm<AliFormType>({
    defaultValues: {
      email: "",
      apiKey: "",
      name: "",
    },
    mode: "onChange",
  });

  const sendLinkMutation = useMutation({
    mutationFn: linkSendCode,
  });

  const saveStoreMutation = useMutation({
    mutationFn: createAliSeller,
    onSuccess: () => onSuccess(),
    onError: (error: any) => {
      if (
        ![
          "verification-ticket-invalid-code",
          "verification-ticket-expired",
          "company-already-linked-to-other-account",
        ].find((v) => v === error?.response?.data?.code)
      ) {
        onError();
      }
    },
  });

  useEffect(() => {
    onStatusChange({
      disabled:
        step === steps.CONFIRM ? confirmState.disabled : Boolean(!isValid),
      isLoading: Boolean(
        sendLinkMutation.isLoading ||
          saveStoreMutation.isLoading ||
          confirmState.loading,
      ),
      title:
        step === steps.CONFIRM ? t("storesPage.addShopModal.confirmBtn") : "",
    });
  }, [
    isValid,
    saveStoreMutation.isLoading,
    confirmState.disabled,
    confirmState.loading,
  ]);

  const onSubmit = (form: AliFormType) => {
    if (step === steps.EMAIL) {
      const isKnownEmail = knownEmails.find((v) => v === form.email);
      if (isKnownEmail) {
        saveStoreMutation.mutate({
          email: form.email,
          token: form.apiKey,
          name: form.name,
          code: null,
          ticketId: null,
        });
      } else {
        sendLinkMutation.mutate(
          {
            email: form.email,
          },
          {
            onSuccess: ({ ticketId }) => {
              setTicket(ticketId);
              setStep(steps.CONFIRM);
            },
          },
        );
      }
    } else if (step === steps.CONFIRM) {
      saveStoreMutation.mutate({
        email: form.email,
        token: form.apiKey,
        name: form.name,
        code: pin,
        ticketId: ticket,
      });
    }
  };

  const submit = handleSubmit(onSubmit);
  const back = () => setStep(steps.EMAIL);

  useImperativeHandle(formRef, () => ({
    submit,
    back: step === steps.CONFIRM ? back : undefined,
  }));

  const validateApiKey = useMemo(
    () =>
      debounce(async (value, resolve) => {
        const result = await queryClient.fetchQuery({
          queryKey: ["validateAliKey", value, ""],
          queryFn: () => validateAliKey({ token: value }),
          staleTime: 5000,
        });

        resolve(
          result?.isSuccess ||
            // Сначала Ali-специфичный текст ошибки, затем общий, затем дефолтный
            t([
              `storesPage.addShopModal.errors.ali.${result.error}`,
              `storesPage.addShopModal.errors.${result.error}`,
              "defaultFormErrors.default",
            ]),
        );
      }, 500),
    [],
  );

  if (step === steps.CONFIRM) {
    const [email] = getValues(["email"]);
    return (
      <ConfirmEmail
        email={email}
        pin={pin}
        setPin={setPin}
        setTicket={setTicket}
        setConfirmState={setConfirmState}
        onSubmit={submit}
        error={saveStoreMutation.isError ? saveStoreMutation.error : undefined}
      />
    );
  }

  return (
    <form className={styles.form} onSubmit={submit}>
      <Alert
        type={AlertType.WARNING}
        title={t('storesPage.addShopModal.apikeyAlertTitle')}
        description={<LinkButton
          to={'https://unitrade-global.com/page139704916.html'}
          title={t('storesPage.addShopModal.apikeyAlertDescription')}
          size={LinkButtonSize.S}
          external
        />}
      />
      <TextInputContainer
        control={control}
        name="name"
        title={t("storesPage.addShopModal.name")}
        placeholder={t("storesPage.addShopModal.namePlaceholder")}
        rules={{ required: t("defaultFormErrors.required") }}
        type="text"
        maxLength={100}
      />
      <div className={styles.row}>
        <TextInputContainer
          control={control}
          name="apiKey"
          title={t("storesPage.addShopModal.apiKeyInput")}
          placeholder="API key"
          rules={{
            required: t("defaultFormErrors.required"),
            validate: async (value) => {
              if (!value) return true;
              return new Promise((resolve) => {
                validateApiKey(value, resolve);
              });
            },
          }}
          type="text"
          maxLength={9999}
        />
        <TextInputContainer
          control={control}
          name="email"
          title={
            <span className={styles.emailTitle}>
              <span>{t("storesPage.addShopModal.emailInput")}</span>
              <Tooltip
                action={
                  <IconButton
                    icon={InfoIcon}
                    onClick={() => {}}
                    size={IconButtonSize.S}
                    type={IconButtonType.ICON}
                  />
                }
                position="top"
                text={t("storesPage.addShopModal.emailTooltip")}
              />
            </span>
          }
          placeholder="Email"
          rules={{ required: t("defaultFormErrors.required") }}
          hasError={sendLinkMutation.isError}
          error={getErrorMessage({
            t,
            error: saveStoreMutation.error || sendLinkMutation.error,
          })}
          type="email"
        />
      </div>
    </form>
  );
};

export default AliForm;
